import {
  Document,
  Text,
  Page,
  StyleSheet,
  Image,
  View,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";
import LogoWhite from "../../assets/images/K2GLogoWhite.png";
import TIMWheel from "../../assets/images/TIMWheel.png";
import TopLogo from "../../assets/images/logo2.png";
import { categoryForSlug } from "../../utils/timFamily";

Font.register({
  family: "Tahoma",
  src: "/fonts/Tahoma-Regular.ttf",
  fonts: [
    { src: "/fonts/Tahoma-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/TAHOMAB0.TTF", fontWeight: "bold" },
  ],
});

/* ─── SECTIONS band helper ─── */
const getSectionBand = (value) => {
  const v = typeof value === "number" ? value : 0;
  if (v < 60) return { label: "OPPORTUNITY", color: "#e67e22" };
  if (v < 90) return { label: "DEVELOPING", color: "#2B8DCC" };
  return { label: "STRENGTH", color: "#69AF45" };
};

/* ───────────────────────────────────────
   Native vertical grouped bar chart for TIM questions
   (Q1, Q2, Q3 ... each with positive & negative bars)
   ─────────────────────────────────────── */
const BAR_MAX = 100;
const POSITIVE_COLOR = "#69AF45";
const NEGATIVE_COLOR = "#2B8DCC";

const NativeTIMQuestionsChart = ({ questions }) => {
  if (!questions || questions.length === 0) return null;

  const chartHeight = 160;
  const labelHeight = 16;
  const legendHeight = 16;
  const barGroupWidth = 100 / questions.length;

  return (
    <View style={{ marginTop: 10, marginBottom: 10 }}>
      {/* Legend */}
      <View style={{ flexDirection: "row", justifyContent: "center", marginBottom: 6 }}>
        <View style={{ flexDirection: "row", alignItems: "center", marginRight: 16 }}>
          <View style={{ width: 10, height: 10, backgroundColor: POSITIVE_COLOR, marginRight: 4 }} />
          <Text style={{ fontSize: 8, color: "#333" }}>Positive</Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View style={{ width: 10, height: 10, backgroundColor: NEGATIVE_COLOR, marginRight: 4 }} />
          <Text style={{ fontSize: 8, color: "#333" }}>Negative</Text>
        </View>
      </View>
      {/* Chart area */}
      <View style={{ height: chartHeight, flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#ccc", borderLeftWidth: 1, borderLeftColor: "#ccc", position: "relative" }}>
        {/* Y-axis labels */}
        {[0, 25, 50, 75, 100].map((tick) => {
          const bottom = (tick / BAR_MAX) * 100;
          return (
            <View key={tick} style={{ position: "absolute", left: -24, bottom: `${bottom}%`, width: 20 }}>
              <Text style={{ fontSize: 6, color: "#888", textAlign: "right" }}>{tick}%</Text>
            </View>
          );
        })}
        {/* Grid lines */}
        {[25, 50, 75, 100].map((tick) => {
          const bottom = (tick / BAR_MAX) * 100;
          return (
            <View key={`grid-${tick}`} style={{ position: "absolute", left: 0, right: 0, bottom: `${bottom}%`, height: 0.5, backgroundColor: "#E0E0E0" }} />
          );
        })}
        {/* Bar groups */}
        {questions.map((q, i) => {
          const posH = Math.max((q.positive / BAR_MAX) * 100, 0);
          const negH = Math.max((q.negative / BAR_MAX) * 100, 0);
          return (
            <View key={i} style={{ width: `${barGroupWidth}%`, flexDirection: "row", alignItems: "flex-end", justifyContent: "center", paddingHorizontal: 2 }}>
              {/* Positive bar */}
              <View style={{ width: "40%", height: `${posH}%`, backgroundColor: POSITIVE_COLOR, marginRight: 1, position: "relative" }}>
                {q.positive > 0 && (
                  <Text style={{ position: "absolute", top: 2, left: 0, right: 0, fontSize: 6, color: "#fff", textAlign: "center", fontWeight: "bold" }}>{q.positive}%</Text>
                )}
              </View>
              {/* Negative bar */}
              <View style={{ width: "40%", height: `${negH}%`, backgroundColor: NEGATIVE_COLOR, position: "relative" }}>
                {q.negative > 0 && (
                  <Text style={{ position: "absolute", top: 2, left: 0, right: 0, fontSize: 6, color: "#fff", textAlign: "center", fontWeight: "bold" }}>{q.negative}%</Text>
                )}
              </View>
            </View>
          );
        })}
      </View>
      {/* X-axis labels */}
      <View style={{ flexDirection: "row", marginTop: 2 }}>
        {questions.map((_, i) => (
          <View key={i} style={{ width: `${barGroupWidth}%`, alignItems: "center" }}>
            <Text style={{ fontSize: 7, fontWeight: "bold", color: "#333" }}>Q{i + 1}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

/* ───────────────────────────────────────
   SVG gauge arc helper for PDF
   ─────────────────────────────────────── */
const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const rad = (a) => ((a - 90) * Math.PI) / 180;
  const x1 = cx + r * Math.cos(rad(startAngle));
  const y1 = cy + r * Math.sin(rad(startAngle));
  const x2 = cx + r * Math.cos(rad(endAngle));
  const y2 = cy + r * Math.sin(rad(endAngle));
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
};

/* ── Single gauge card for PDF ── */
const PdfGaugeCard = ({ block }) => {
  const posPct = Math.min(Math.max(block.positive || 0, 0), 100);
  const displayPct = Number.isInteger(posPct) ? posPct : posPct.toFixed(1);
  const band = getSectionBand(posPct);
  const cx = 40;
  const cy = 40;
  const r = 28;
  const strokeW = 7;
  const valueAngle = -90 + (posPct / 100) * 180;

  return (
    <View style={{ width: "30%", alignItems: "center", marginBottom: 4, padding: 3 }}>
      <Svg width={80} height={46} viewBox="0 0 80 46">
        {/* Background arc (gray) */}
        <Path d={describeArc(cx, cy, r, -90, 90)} fill="none" stroke="#E0E0E0" strokeWidth={strokeW} strokeLinecap="round" />
        {/* Value arc (colored) */}
        {posPct > 0 && (
          <Path d={describeArc(cx, cy, r, -90, valueAngle)} fill="none" stroke={band.color} strokeWidth={strokeW} strokeLinecap="round" />
        )}
      </Svg>
      <Text style={{ fontSize: 13, fontWeight: "bold", color: band.color, textAlign: "center", marginTop: -4 }}>{displayPct}%</Text>
      <Text style={{ fontSize: 7, color: "#333", textAlign: "center", marginTop: 2, maxWidth: 110 }}>{block.name}</Text>
      {band.label ? (
        <Text style={{ fontSize: 7, fontWeight: "bold", color: band.color, textAlign: "center", marginTop: 1 }}>{band.label}</Text>
      ) : null}
    </View>
  );
};

/* ── Gauge cards grid for PDF ── */
const NativeTIMBlocksChart = ({ blocksData }) => {
  if (!blocksData || blocksData.length === 0) return null;

  // Split into rows of 3
  const rows = [];
  for (let i = 0; i < blocksData.length; i += 3) {
    rows.push(blocksData.slice(i, i + 3));
  }

  return (
    <View style={{ marginTop: 10, marginBottom: 6 }}>
      {rows.map((row, rowIdx) => (
        <View key={rowIdx} style={{ flexDirection: "row", justifyContent: "center", marginBottom: 2 }}>
          {row.map((b, i) => (
            <PdfGaugeCard key={i} block={b} />
          ))}
          {/* Fill empty space if row has fewer than 3 items */}
          {row.length < 3 && Array.from({ length: 3 - row.length }).map((_, i) => (
            <View key={`empty-${i}`} style={{ width: "30%" }} />
          ))}
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 50,
    fontFamily: "Tahoma",
  },
  sectionA: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 20,
    alignItems: "center",
  },
  sectionB: {
    width: "100%",
    marginBottom: 20,
  },
  title: { fontSize: 18, marginBottom: 10 },
  mainTitle: {
    fontSize: 30,
    fontWeight: 600,
    textAlign: "center",
    color: "#0F5385",
    width: "100%",
    marginTop: 40,
    marginLeft: 20,
    flexShrink: 1,
  },
  mainTitle_green: {
    fontSize: 30,
    fontWeight: 600,
    textAlign: "center",
    color: "#69AF45",
    width: "100%",
    marginLeft: 20,
    flexShrink: 1,
  },
  subTitle: {
    fontSize: 30,
    fontWeight: 700,
    textAlign: "center",
    color: "#0F5385",
    width: "100%",
    marginTop: 10,
    marginLeft: 20,
    flexShrink: 1,
  },
  subTitle_green: {
    fontSize: 25,
    fontWeight: 400,
    textAlign: "center",
    color: "#69AF45",
    width: "100%",
    marginLeft: 20,
    flexShrink: 1,
  },
  userName: {
    fontSize: 20,
    fontWeight: 400,
    textAlign: "center",
    color: "#0F5385",
    width: "70%",
    marginLeft: 20,
    marginBottom: 40,
    flexShrink: 1,
  },
  secondaryTitle: {
    fontSize: 30,
    fontWeight: 600,
    textAlign: "center",
    color: "#0F5385",
  },
  header: {
    fontSize: 20,
    fontWeight: 600,
    color: "#0F5385",
    marginTop: 20,
  },
  headerX: {
    fontSize: 20,
    fontWeight: 600,
    color: "#0F5385",
    marginTop: 5,
    width: "100%",
  },
  header2: {
    fontSize: 17,
    fontWeight: 500,
    color: "#0F5385",
    marginTop: 15,
    marginBottom: 0,
  },
  header3: {
    fontSize: 15,
    fontWeight: 500,
    color: "#0F5385",
    marginTop: 15,
  },
  normalText: {
    fontSize: 12,
    fontWeight: 400,
    flexShrink: 1,
    marginTop: 20,
    lineHeight: 1.4,
  },
  questionsText: {
    fontSize: 12,
    fontWeight: 300,
    flexShrink: 1,
    marginTop: 10,
    lineHeight: 1,
  },
  questionRow: {
    flexDirection: "row",
    marginTop: 10,
  },
  questionLabel: {
    fontSize: 12,
    fontWeight: 300,
    width: 26,
    lineHeight: 1.2,
  },
  questionBody: {
    fontSize: 12,
    fontWeight: 300,
    flex: 1,
    lineHeight: 1.2,
  },
  mainImage: {
    width: "15%",
  },
  wheelImageContainer: {
    alignItems: "center",
    marginTop: 20,
  },
  wheelImage: {
    width: 360,
  },
  chart: { width: 500, marginTop: 5, marginBottom: 10 },
  chartSummary: { width: 400 },
  listContainer: {
    marginBottom: 10,
    marginTop: 40,
  },
  listItem: {
    flexDirection: "row",
    marginBottom: 20,
    alignItems: "flex-start",
  },

  listBullet: {
    width: 15,
    fontSize: 10,
  },
  listItemText: {
    flex: 1,
    fontSize: 12,
  },
  bannerContainer: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    width: "100%",
  },
  bannerTitle: {
    fontSize: 20,
    color: "white",
    textAlign: "center",
    fontWeight: "bold",
  },
  bannerSubtitle: {
    fontSize: 17,
    color: "white",
    textAlign: "left",
    fontWeight: 300,
  },
  table: {
    display: "flex",
    width: "65%",
    flexDirection: "column",
    margin: "16 auto",
    borderRadius: 6,
    overflow: "hidden",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    fontWeight: "bold",
    padding: 8,
    fontSize: 10,
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
  },
  cellCol1: {
    width: "55%",
  },
  cellCol2: {
    width: "45%",
  },
  headerInfo: {
    marginTop: 30, // separación del banner
    marginBottom: 30,
    alignItems: "flex-start", // que el texto quede alineado a la izquierda
  },
  headerName: {
    fontSize: 24,
    fontWeight: 600,
    color: "#0F5385",
    marginLeft: 40,
  },
  headerTeam: {
    fontSize: 16,
    fontWeight: 600,
    color: "#0F5385",
    marginTop: 4,
    marginLeft: 40,
  },
  headerDate: {
    fontSize: 14,
    fontWeight: 400,
    color: "#69AF45",
    marginTop: 4,
    marginLeft: 40,
  },
  pageNumbers: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 20,
    textAlign: "right",
    fontSize: 10,
  },
  pageGroupName: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 10,
    textAlign: "center",
  },
  heroBanner: {
    width: "100%",
    height: 120,
    backgroundColor: "#0F5385", // azul de fondo
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 35,
  },
  heroImage: {
    width: 80,
    height: 80,
    marginRight: 20,
  },
  heroTextContainer: {
    flex: 1,
  },
  heroTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 6,
  },
  heroSubtitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#FFFFFF",
  },
  footerImage: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: 70,
  },
  footerImageBulb: {
    position: "absolute",
    bottom: 0,
    left: 20,
    width: "auto",
    height: 80,
  },
  subTitleM: {
    fontSize: 30,
    fontWeight: 700,
    textAlign: "center",
    color: "#0F5385",
  },
  sup: {
    fontSize: 10, // más pequeño para simular superíndice
    marginTop: -50, // ajusta para que se vea en altura correcta
  },
  bannerTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  bannerTitleMain: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  bannerTitleSup: {
    fontSize: 14, // más pequeño
    fontWeight: "bold",
    color: "#FFFFFF",
    marginLeft: 2, // pequeño espacio a la derecha del texto
    marginTop: 4, // ajusta esto para que se vea como superíndice
  },
  textRow: {
    flexDirection: "row",
    alignItems: "flex-start", // alinea la parte alta de los textos
  },
  textMain: {
    fontWeight: 600,
    color: "#0F5385",
  },
  textSup: {
    fontWeight: "bold",
    color: "#0F5385",
    marginLeft: 2, // pequeño espacio a la derecha del texto
  },
});

const ColorBanner = ({ title, color }) => (
  <View style={[styles.bannerContainer, { backgroundColor: color }]}>
    <Text style={styles.bannerTitle}>{title}</Text>
  </View>
);

const titleWithSup = (text) => {
  const [before, after] = text.split("<sup>&reg;</sup>");

  return (
    <View style={styles.bannerTitleRow}>
      <Text style={styles.bannerTitleMain}>{before}</Text>
      <Text style={styles.bannerTitleSup}>®</Text>
      {after ? <Text style={styles.bannerTitleMain}>{after}</Text> : null}
    </View>
  );
};

const MainBanner = ({ title, subtitle, color }) => (
  <View style={styles.heroBanner}>
    <View style={styles.heroTextContainer}>
      {titleWithSup(title)}
      <Text style={styles.bannerSubtitle}>{subtitle}</Text>
    </View>
    <Image src={LogoWhite} style={styles.mainImage} />
  </View>
);

const DEFAULT_THRESHOLDS = {
  strength: { label: "STRENGTH", minScore: 90 },
  developing: { label: "DEVELOPING", minScore: 60 },
  opportunity: { label: "OPPORTUNITY" },
};

const resolveThresholds = (report) => {
  const t = report?.ratingThresholds;
  return {
    strength: {
      label: t?.strength?.label || DEFAULT_THRESHOLDS.strength.label,
      minScore: typeof t?.strength?.minScore === "number" ? t.strength.minScore : DEFAULT_THRESHOLDS.strength.minScore,
    },
    developing: {
      label: t?.developing?.label || DEFAULT_THRESHOLDS.developing.label,
      minScore: typeof t?.developing?.minScore === "number" ? t.developing.minScore : DEFAULT_THRESHOLDS.developing.minScore,
    },
    opportunity: {
      label: t?.opportunity?.label || DEFAULT_THRESHOLDS.opportunity.label,
    },
  };
};

const buildTableData = (thresholds) => {
  const t = thresholds || DEFAULT_THRESHOLDS;
  const developingMin = t.developing.minScore;
  const strengthMin = t.strength.minScore;
  const developingMax = strengthMin - 1;
  return [
    { score: `${strengthMin}% or higher`, rating: t.strength.label, color: "#69AF45", bgColor: "#f0f7f0" },
    { score: `${developingMin}%–${developingMax}%`, rating: t.developing.label, color: "#2B8DCC", bgColor: "#f5f9fc" },
    { score: `Less than ${developingMin}%`, rating: t.opportunity.label, color: "#e67e22", bgColor: "#fef8f0" },
  ];
};

const RatingTable = ({ thresholds }) => {
  const tableData = buildTableData(thresholds);
  return (
  <View style={styles.table}>
    {/* Header row */}
    <View style={[styles.tableRow, { backgroundColor: "#0F5385", borderTopLeftRadius: 6, borderTopRightRadius: 6 }]}>
      <View style={styles.cellCol1}>
        <Text style={{ fontSize: 10, fontWeight: "bold", color: "#FFFFFF", padding: 8 }}>
          Score (% of positive responses)
        </Text>
      </View>
      <View style={styles.cellCol2}>
        <Text style={{ fontSize: 10, fontWeight: "bold", color: "#FFFFFF", padding: 8 }}>Rating</Text>
      </View>
    </View>

    {/* Body rows */}
    {tableData.map((item, index) => (
      <View key={index} style={[styles.tableRow, { backgroundColor: item.bgColor }]}>
        <View style={styles.cellCol1}>
          <Text style={{ fontSize: 10, color: "#333", padding: 8 }}>{item.score}</Text>
        </View>
        <View style={styles.cellCol2}>
          <Text style={{ fontSize: 10, fontWeight: "bold", color: item.color, padding: 8 }}>{item.rating}</Text>
        </View>
      </View>
    ))}
  </View>
  );
};

const AssessmentTIMPDF = ({
  name,
  report,
  blocks,
  questionsData,
  blocksOverallData,
  info,
  feedbackByBlock,
  groupName,
  companyName,
  dateCompleted,
  extraQ,
  blockReflections,
  assessmentSlug,
  textOverrides = {},
}) => {
  const thresholds = resolveThresholds(report);
  // Admin report-editor overrides (per-team). ov(key, default) returns the
  // curated text if the admin edited it, otherwise the original.
  const ov = (key, def) =>
    Object.prototype.hasOwnProperty.call(textOverrides, key)
      ? textOverrides[key]
      : def;
  // Executive report tweaks (per client feedback): rename the senior-block
  // header to "Organizational Perspective", drop its descriptive blurbs, and
  // omit the per-section reflection prompt. TIM reports are unaffected.
  const isExecutive = categoryForSlug(assessmentSlug) === "executive";
  const coverSubtitle = report?.coverSubtitle || "Team Effectiveness Health Check Report";

  const cleanTitle = (text) => {
    return text.replace("<sup>&reg;</sup>", "®").replace("<sup>®</sup>", "®");
  };

  const textWithSup = (text, size = 20, justify = "flex-start") => {
    const [before, after] = text.split("<sup>&reg;</sup>");

    const supSize = Math.round(size * 0.55); // 55% del tamaño principal
    const supOffset = Math.round(size * 0.12); // subirlo un poco relativo al tamaño

    return (
      <View style={[styles.textRow, { justifyContent: justify }]}>
        {/* Texto principal */}
        <Text style={[styles.textMain, { fontSize: size }]}>{before}</Text>

        {/* Superíndice ® */}
        <Text
          style={[styles.textSup, { fontSize: supSize, marginTop: -supOffset }]}
        >
          ®
        </Text>

        {/* Texto después del sup (si existe) */}
        {after ? (
          <Text style={[styles.textMain, { fontSize: size }]}>{after}</Text>
        ) : null}
      </View>
    );
  };

  const findInfoText = (blockId) => {
    const i = info.find((b) => b.block === blockId);
    return ov(`block.${blockId}.info`, i?.text || "");
  };

  return (
    <Document>
      <Page>
        <MainBanner
          title={"Teams in Motion<sup>&reg;</sup>"}
          subtitle={coverSubtitle}
          color={"#105385"}
        />
        <View style={styles.headerInfo}>
          {companyName && (
            <Text style={styles.headerName}>Company: {companyName}</Text>
          )}
          <Text style={styles.headerTeam}>Team: {groupName}</Text>
          <Text style={styles.headerDate}>
            Date Completed: {dateCompleted || "N/A"}
          </Text>
        </View>
        <View style={styles.wheelImageContainer}>
          <Image src={TIMWheel} style={styles.wheelImage} />
        </View>
        {/* Page number display */}
        <Text
          style={styles.pageNumbers}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* Group Name display */}
        <Text
          style={styles.pageGroupName}
          render={(_) => `${groupName || ""}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* FOOTER CON IMAGEN */}
        {/* <Image src={FooterTrees} style={styles.footerImage}/> */}
        {/* TOP CON IMAGEN */}
        <Image src={TopLogo} style={styles.footerImageBulb} fixed />
      </Page>
      <Page style={styles.page}>
        <View style={styles.sectionB}>
          {textWithSup("Teams in Motion<sup>&reg;</sup>", 30, "center")}
          <Text style={styles.headerX}>Team Health Check</Text>
          <Text
            style={[styles.normalText, { marginBottom: 20, marginTop: "5px" }]}
          >
            {cleanTitle(report.welcome)}
          </Text>
          <ColorBanner
            title={"Effective Team Practices = High Performance"}
            color={"#0F5385"}
          />
          <Text style={styles.normalText}>
            {ov(
              "special.fiveCategories.intro",
              "The five categories that reflect the practices of high performing teams are as follows:",
            )}
          </Text>
          <Text style={[styles.normalText, { marginLeft: "10px" }]}>
            1. Culture of Trust & Psychological Safety
          </Text>
          <Text style={[styles.normalText, { marginLeft: "10px" }]}>
            2. Constructive Conversations
          </Text>
          <Text style={[styles.normalText, { marginLeft: "10px" }]}>
            3. Collaborative Decision Making
          </Text>
          <Text style={[styles.normalText, { marginLeft: "10px" }]}>
            4. Cohesive Commitment
          </Text>
          <Text
            style={[
              styles.normalText,
              { marginBottom: 20, marginLeft: "10px" },
            ]}
          >
            5. Clear Communication Inside and Outside the Team
          </Text>
          {textWithSup("Format of the Teams in Motion<sup>&reg;</sup>", 20)}
          <Text style={styles.headerX}>Team Health Check</Text>
          <Text style={[styles.normalText, { marginTop: "5px" }]}>
            {ov(
              "special.methodology.body",
              "There are 50 total questions on the standard Health Check that are administered to all who take the assessment. Each question asks the team member to rate a statement based on a four-point scale, forcing a positive or negative bent. The rating scale is: Strongly Disagree, Disagree, Agree, Strongly Agree. There is also an option for the respondent to designate “Unable to Reply” on each question. These “Unable to Reply” responses are eliminated from the results calculation. For performance indicators, each team member is asked to rate their perception of team effectiveness in terms of results the team achieves, efficiency of achieving the results, and a rating of the personal experience working on the team. In most companies, we also gather executive-level performance ratings of each team that helps us correlate the specific practices with high-performing teams.",
            )}
          </Text>
        </View>
        {/* Page number display */}
        <Text
          style={styles.pageNumbers}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* Group Name display */}
        <Text
          style={styles.pageGroupName}
          render={(_) => `${groupName || ""}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* FOOTER CON IMAGEN */}
        {/* <Image src={FooterTrees} style={styles.footerImage}/> */}
        {/* TOP CON IMAGEN */}
        <Image src={TopLogo} style={styles.footerImageBulb} fixed />
      </Page>
      <Page style={styles.page}>
        <Text style={[styles.header, { marginTop: "40px" }]}>
          Team Effectiveness Reporting
        </Text>
        <Text style={[styles.header2, { marginTop: "10px" }]}>
          Company versus Team
        </Text>
        <Text style={[styles.normalText, { marginTop: "5px" }]}>
          {ov(
            "special.companyTeam.body",
            "The report can be generated at a company-level, as a compilation of all teams in the company, or at the team level where the assessment responses are reported as a compilation of team members on a particular team. The purpose of the company-level assessment results is to help an organization’s senior leaders prioritize organizational development investments. The assessment results will highlight organization-wide developmental needs that can be turned into customized programs for company-wide delivery. The team-level assessment results can be delivered directly to an intact team for purposes of team-specific development and coaching. It is critical the Team Leader be involved in the process of reviewing, interpreting, and sharing the results with the intact team. The team-level results can be turned into a customized team development program including training, in-person team coaching, and customized Team Leader coaching.",
          )}
        </Text>
        <Text style={[styles.header2, { marginTop: "10px" }]}>
          For Developmental Purposes!
        </Text>
        <Text style={[styles.normalText, { marginTop: "5px" }]}>
          Warning – these results should NOT be used as a performance rating
          tool for a team, a Team Leader, or an organization! The assessment
          includes questions around the factors that are proven to drive team
          effectiveness, and for that reason is designed as a developmental tool
          to increase the effectiveness of your teams! Please review the results
          of the report and then engage the support of a Teams In Motion
          <Text style={{ fontSize: 7, verticalAlign: "super" }}>®</Text>{" "}
          facilitator to complete the interpretation and discuss the plan of
          action based on the assessment results. An investment in developing
          these competencies within your teams will lead to measurable
          performance results! Make the investment count.
        </Text>
        <Text style={styles.normalText}>
          Keep in mind that your team is assessed as a full team, not as
          individual team members.
        </Text>
        {/* Page number display */}
        <Text
          style={styles.pageNumbers}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* Group Name display */}
        <Text
          style={styles.pageGroupName}
          render={(_) => `${groupName || ""}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* FOOTER CON IMAGEN */}
        {/* <Image src={FooterTrees} style={styles.footerImage}/> */}
        {/* TOP CON IMAGEN */}
        <Image src={TopLogo} style={styles.footerImageBulb} fixed />
      </Page>
      <Page style={styles.page}>
        <ColorBanner
          title={"Team Effectiveness:\nTeam Health Check Summary"}
          color={"#0F5385"}
        />
        <Text style={styles.normalText}>
          {ov(
            "summary.intro",
            "The gauges below reflect your team's overall scores for each category. The percentage shown is the % of positive responses (defined as Agree + Strongly Agree). In the following pages you will find question by question scores.",
          )}
        </Text>
        <Text style={styles.normalText}>
          {ov(
            "summary.levels.intro",
            "For developmental purposes, we categorize your team's scores into three different levels to help you focus your development priorities.",
          )}
        </Text>
        <RatingTable thresholds={thresholds} />
        {blocksOverallData && blocksOverallData.length > 0 && (
          <NativeTIMBlocksChart blocksData={blocksOverallData} />
        )}
        {/* Page number display */}
        <Text
          style={styles.pageNumbers}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* Group Name display */}
        <Text
          style={styles.pageGroupName}
          render={(_) => `${groupName || ""}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* FOOTER CON IMAGEN */}
        {/* <Image src={FooterTrees} style={styles.footerImage}/> */}
        {/* TOP CON IMAGEN */}
        <Image src={TopLogo} style={styles.footerImageBulb} fixed />
      </Page>
      {blocks.map((block) => {
        const feedback = feedbackByBlock[block._id] || [];
        const blockQuestions = questionsData[block._id] || [];
        const displayQuestions =
          block.slug !== "senior_leader_survey_questions"
            ? block.questions
            : block.questions.slice(0, -1);
        const reflectionAnswer = blockReflections?.[block._id];
        // These two blocks are dense enough that the Reflection heading
        // collides with the page footer when an answer is present. Push the
        // Reflection + answer onto a dedicated page (only when there's text
        // to show).
        const reflectionOnOwnPage =
          !!reflectionAnswer &&
          (block.slug === "senior_leader_survey_questions" ||
            /clear\s+communication/i.test(block.name || ""));
        return (
          <Page key={block._id} style={styles.page}>
            <ColorBanner title={block.name} color={block.color} />
            {feedback.length > 0 &&
              (isExecutive ? (
                <Text style={styles.header}>Organizational Perspective</Text>
              ) : (
                <>
                  <Text style={styles.header}>Senior Leader Perspective</Text>
                  <Text style={styles.normalText}>
                    This section provides a complementary view of team
                    effectiveness through the eyes of senior leaders. It
                    reflects their assessment of the team's quality, efficiency,
                    innovation, decision escalation, and overall alignment with
                    organizational expectations. This perspective adds a
                    critical external lens, ensuring the team understands how
                    its performance is perceived by those who rely on its
                    results and leadership impact.{" "}
                  </Text>
                </>
              ))}
            <Text style={[styles.normalText, { marginBottom: 20 }]}>
              {findInfoText(block._id)}
            </Text>
            {blockQuestions.length > 0 ? (
              <NativeTIMQuestionsChart questions={blockQuestions} />
            ) : (
              <Text>Graph not available for this section.</Text>
            )}
            {displayQuestions.map((q, i) => (
              <View
                key={i}
                style={styles.questionRow}
                /* On the Executive senior section, break before Q8 so Q8-Q10
                   move to the next page instead of colliding with the footer
                   logo, and so the Verbatim Comments have room below them. */
                break={
                  isExecutive &&
                  block.slug === "senior_leader_survey_questions" &&
                  i === 7
                }
              >
                <Text style={styles.questionLabel}>Q{i + 1}.</Text>
                <Text style={styles.questionBody}>{q.text}</Text>
              </View>
            ))}
            {/* Feedback (solo si existe). On the Executive report, start the
                Verbatim Comments on a fresh page so the header doesn't get
                orphaned at the bottom of the questions page. */}
            {feedback.length > 0 && (
              <View>
                <Text style={styles.header}>Verbatim Comments</Text>
                {feedback.map((item, idx) => (
                  <Text
                    key={idx}
                    style={[
                      styles.normalText,
                      { textAlign: "left", width: "100%", marginLeft: 10 },
                    ]}
                  >
                    • {item.answer}
                  </Text>
                ))}
              </View>
            )}

            {!isExecutive && (
              <View break={reflectionOnOwnPage} style={{ marginTop: 10 }}>
                <Text style={[styles.header3, { marginTop: 0 }]}>
                  Reflection: Which of the items in this dimension resonate most
                  as a high-priority focus area?
                </Text>
                {reflectionAnswer ? (
                  <Text style={[styles.normalText, { marginTop: 4 }]}>
                    {reflectionAnswer}
                  </Text>
                ) : null}
              </View>
            )}

            {/* Page number display */}
            <Text
              style={styles.pageNumbers}
              render={({ pageNumber }) => `${pageNumber}`}
              fixed // Ensures the page number is rendered consistently on each page
            />
            {/* Group Name display */}
            <Text
              style={styles.pageGroupName}
              render={(_) => `${groupName || ""}`}
              fixed // Ensures the page number is rendered consistently on each page
            />
            {/* FOOTER CON IMAGEN */}
            {/* <Image src={FooterTrees} style={styles.footerImage}/> */}
            {/* TOP CON IMAGEN */}
            <Image src={TopLogo} style={styles.footerImageBulb} fixed />
          </Page>
        );
      })}
      <Page style={styles.page}>
        <ColorBanner title={"Team Development Planning"} color={"#0F5385"} />
        <Text style={styles.normalText}>
          {ov(
            "end.planning.intro",
            "Every team is constantly changing and growing. Expert Teams make continuous improvement an intentional part of how they operate. After reviewing your team results, consider the following table as a way to prioritize your team’s focus areas.",
          )}
        </Text>
        <Text style={styles.normalText}>
          {ov(
            "summary.levels.intro",
            "For developmental purposes, we categorize your team’s scores into three different levels to help you focus your development priorities.",
          )}
        </Text>
        <RatingTable thresholds={thresholds} />
        <Text style={{ marginTop: 20, marginBottom: 20 }}></Text>
        <Text style={styles.header2}>
          {ov("end.priorities.label", "Identify your team's top 3 priorities")}
        </Text>
        <Text style={styles.normalText}>{extraQ}</Text>
        <Text style={[styles.normalText, { marginTop: "100px" }]}>
          If your team is already enrolled in the Know2Grow Teams in Motion
          <Text style={{ fontSize: 7, verticalAlign: "super" }}>®</Text> program,
          use these suggestions to focus your efforts. To enroll in the
          Teams in Motion
          <Text style={{ fontSize: 7, verticalAlign: "super" }}>®</Text> program,
          visit Know2Grow.com.
        </Text>
        {/* Page number display */}
        <Text
          style={styles.pageNumbers}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* Group Name display */}
        <Text
          style={styles.pageGroupName}
          render={(_) => `${groupName || ""}`}
          fixed // Ensures the page number is rendered consistently on each page
        />
        {/* FOOTER CON IMAGEN */}
        {/* <Image src={FooterTrees} style={styles.footerImage}/> */}
        {/* TOP CON IMAGEN */}
        <Image src={TopLogo} style={styles.footerImageBulb} fixed />
      </Page>
    </Document>
  );
};

export default AssessmentTIMPDF;
